/**
 * Etwas in die Karte zeichnen, sobald sie es annimmt — und nie später als das.
 *
 * ## Der Fehler, gegen den das gebaut ist
 *
 * Beide Zeichen-Effekte der Karte — Führungsspur und Navigationsweg — standen
 * bis zum 04.09.2026 so da:
 *
 * ```ts
 * if (karte.isStyleLoaded()) {
 *   zeichnen();
 * } else {
 *   karte.once('load', zeichnen);
 *   karte.once('styledata', zeichnen);
 * }
 * ```
 *
 * Drei Dinge kommen darin zusammen, und einzeln wäre keines schlimm:
 *
 *   1. **`isStyleLoaded()` ist viel öfter falsch als es klingt.** Es ist nicht
 *      „der Stil steht", sondern zusätzlich „**keine Quelle lädt gerade**":
 *      `Style.loaded()` gibt `false` zurück, solange irgendein Kachelverwalter
 *      noch lädt. Auf einem fahrenden Gerät lädt praktisch immer eine Kachel.
 *      Der `else`-Zweig ist damit der Normalfall, nicht die Ausnahme.
 *   2. **`zeichnen` hält die Angaben fest, mit denen es angemeldet wurde.** Es
 *      ist ein Abschluss über `props.route` beziehungsweise `props.track` aus
 *      genau diesem Bild.
 *   3. **Abgemeldet wurde nichts.** Ein Effekt in React räumt auf, indem er eine
 *      Funktion zurückgibt; diese beiden gaben keine zurück.
 *
 * Zusammen heißt das: Jede Neuberechnung der Route — alle fünf Sekunden —
 * hinterlässt zwei Hörer mit einem veralteten Weg, die bei der nächsten
 * Stiländerung auslösen. Das ist ein Fehler, und die Aufräumfunktion unten
 * behebt ihn.
 *
 * **Die hängengebliebene blaue Linie erklärt er aber nicht.** Das stand hier
 * bis zum 11.09.2026 (A-104), samt der Behauptung, `setData` löse `styledata`
 * aus. Beides war falsch: Eine GeoJSON-Quelle feuert nach `setData` Ereignisse
 * mit `dataType: 'source'`, und die Karte macht daraus `sourcedata`, nie
 * `styledata`. Auf der Ausfahrt 639521 blieb die Linie trotz dieser Behebung
 * stehen. Der eigentliche Grund steht bei `setzeOderLegeAn` (A-109).
 *
 * ## Was hier stattdessen geschieht
 *
 * Gezeichnet wird **einmal**, und die Anmeldung endet mit dem Effekt, der sie
 * gemacht hat. Der Rückgabewert ist die Aufräumfunktion und gehört unmittelbar
 * in das `return` des Effekts — dann kann kein Hörer eines vorigen Bildes mehr
 * auslösen.
 */

/**
 * Was von der Karte gebraucht wird.
 *
 * Bewusst nur diese drei Aussagen und nicht `MapLibreMap`: So ist die Regel
 * ohne Browser und ohne Karte prüfbar — genau das, was Abschnitt 4 des Auftrags
 * von allem verlangt, was nicht Darstellung ist.
 */
export interface Kartenziel {
  /*
   * `void | boolean` und nicht `boolean | undefined`: So schreibt MapLibre es —
   * ohne Stil verlässt die Methode sich mit einem nackten `return`. Deshalb
   * wird unten auf `=== true` geprüft und nicht auf Wahrheit.
   */
  isStyleLoaded(): boolean | void;
  on(ereignis: 'load' | 'styledata', hoerer: () => void): unknown;
  off(ereignis: 'load' | 'styledata', hoerer: () => void): unknown;
}

/**
 * Ruft `zeichnen` sofort auf, wenn die Karte bereit ist, sonst beim nächsten
 * Mal, an dem sie es meldet — und höchstens einmal.
 *
 * Gibt die Aufräumfunktion zurück. Sie ist auch dann gefahrlos aufzurufen, wenn
 * bereits gezeichnet wurde.
 */
export function zeichneWennBereit(karte: Kartenziel, zeichnen: () => void): () => void {
  if (karte.isStyleLoaded() === true) {
    zeichnen();
    return () => undefined;
  }

  let erledigt = false;

  const abmelden = (): void => {
    karte.off('load', einmal);
    karte.off('styledata', einmal);
  };

  /*
   * Beide Ereignisse führen zum selben Ziel, und es soll nur eines zählen:
   * `load` kommt genau einmal im Leben der Karte, `styledata` bei jeder
   * Stiländerung. Ohne diesen Riegel zeichnete ein Aufruf zweimal.
   */
  function einmal(): void {
    if (erledigt) {
      return;
    }
    erledigt = true;
    abmelden();
    zeichnen();
  }

  karte.on('load', einmal);
  karte.on('styledata', einmal);

  return abmelden;
}

/**
 * Aktualisiert eine Quelle, die es schon gibt, **sofort** — und wartet nur mit
 * dem ersten Anlegen, bis der Stil es annimmt.
 *
 * ## Warum die Linie nach dem Ende einer Navigation stehen blieb (A-109)
 *
 * Drei Dinge, alle in der Quelle von MapLibre nachgelesen:
 *
 *   1. `isStyleLoaded()` ist falsch, solange **irgendeine Kachel** lädt —
 *      `Style.loaded()` läuft über alle Kachelverwalter. Bei 75 km/h lädt
 *      praktisch immer eine.
 *   2. War sie falsch, wurde das Zeichnen auf `load` oder `styledata`
 *      verschoben. `load` kommt einmal im Leben der Karte.
 *   3. `setData` erzeugt nur `sourcedata`, **nie** `styledata`.
 *
 * Endet die Navigation, wird die Linie genau einmal abgeräumt. Lud in diesem
 * Augenblick eine Kachel, wartete das Abräumen auf ein Ereignis, das keine
 * Linie mehr auslösen konnte — und die letzte Route blieb stehen. Auf der
 * Ausfahrt 639521 am 11.09.2026 zweimal fotografiert, jeweils Sekunden nach
 * dem Ende der Navigation (12:57:05 und 13:10:13).
 *
 * ## Was stattdessen gilt
 *
 * Gewartet werden muss nur fürs **Anlegen**: `addSource` prüft über
 * `_checkLoaded()`, ob der Stil geladen ist, und wirft sonst. `getSource` und
 * `setData` prüfen nichts dergleichen. Eine Quelle, die es schon gibt, wird
 * deshalb sofort aktualisiert — auch das Leeren am Ende. Verschoben wird nur,
 * was noch nicht existiert: beim ersten Mal, und nach dem Wechsel auf die
 * dunkle Karte, denn `setStyle` wirft alle Quellen weg.
 *
 * `aktualisiere` gibt zurück, ob es die Quelle gab. `anlegen` legt Quelle und
 * Ebenen an. Zurück kommt die Aufräumfunktion für den Effekt.
 */
export function setzeOderLegeAn(
  karte: Kartenziel,
  aktualisiere: () => boolean,
  anlegen: () => void,
): () => void {
  if (aktualisiere()) {
    return () => undefined;
  }
  return zeichneWennBereit(karte, () => {
    // Ist die Quelle beim Warten doch entstanden, gilt dieselbe Regel: nur
    // aktualisieren. Ein zweites `addSource` würfe „Source already exists".
    if (!aktualisiere()) {
      anlegen();
    }
  });
}
